import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, Users, Eye, Edit, Trash2, UserPlus, Lock, Unlock,
  LayoutDashboard, Phone, Building2, Briefcase, Calendar,
  Settings, Plug, TrendingUp, GitBranch, Download, Megaphone,
  Share2, Sparkles, KanbanSquare, Save, RotateCcw, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  LayoutDashboard, Phone, Building2, Briefcase, Calendar, Building: Building2,
  Settings, Plug, TrendingUp, GitBranch, Download, Megaphone, UsersRound: Users,
  Share2, Sparkles, KanbanSquare, Users, UserPlus
};

const RBACSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rbacSettings, setRbacSettings] = useState(null);
  const [selectedRole, setSelectedRole] = useState('sales_manager');
  const [editedPermissions, setEditedPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  useEffect(() => {
    fetchRBACSettings();
  }, []);

  const fetchRBACSettings = async () => {
    try {
      const response = await axios.get(`${API}/rbac/settings?token=${token}`);
      setRbacSettings(response.data);
      if (response.data.roles && response.data.roles[selectedRole]) {
        setEditedPermissions(JSON.parse(JSON.stringify(response.data.roles[selectedRole])));
      }
    } catch (error) {
      console.error('Failed to fetch RBAC settings:', error);
      toast.error('Failed to load access control settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role) => {
    if (hasChanges) {
      toast.warning('You have unsaved changes. Please save or discard them first.');
      return;
    }
    setSelectedRole(role);
    if (rbacSettings?.roles?.[role]) {
      setEditedPermissions(JSON.parse(JSON.stringify(rbacSettings.roles[role])));
    }
    setHasChanges(false);
  };

  const handleFeatureToggle = (feature, enabled) => {
    setEditedPermissions(prev => ({
      ...prev,
      feature_access: {
        ...prev.feature_access,
        [feature]: enabled
      }
    }));
    setHasChanges(true);
  };

  const handleDataAccessChange = (resource, field, value) => {
    setEditedPermissions(prev => ({
      ...prev,
      data_access: {
        ...prev.data_access,
        [resource]: {
          ...prev.data_access?.[resource],
          [field]: value
        }
      }
    }));
    setHasChanges(true);
  };

  const handleActionToggle = (action, enabled) => {
    setEditedPermissions(prev => ({
      ...prev,
      actions: {
        ...prev.actions,
        [action]: enabled
      }
    }));
    setHasChanges(true);
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/rbac/roles/${selectedRole}?token=${token}`,
        editedPermissions
      );
      
      if (response.data.success) {
        toast.success(`Permissions for ${rbacSettings.roles[selectedRole]?.name || selectedRole} saved`);
        setHasChanges(false);
        fetchRBACSettings();
      } else {
        toast.error(response.data.error || 'Failed to save permissions');
      }
    } catch (error) {
      console.error('Failed to save permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/rbac/roles/${selectedRole}/reset?token=${token}`
      );
      
      if (response.data.success) {
        toast.success(`Permissions for ${selectedRole} reset to default`);
        setHasChanges(false);
        setShowResetDialog(false);
        fetchRBACSettings();
      } else {
        toast.error(response.data.error || 'Failed to reset permissions');
      }
    } catch (error) {
      console.error('Failed to reset permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    if (rbacSettings?.roles?.[selectedRole]) {
      setEditedPermissions(JSON.parse(JSON.stringify(rbacSettings.roles[selectedRole])));
    }
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!rbacSettings?.has_team) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Team Found</h3>
            <p className="text-slate-500">Create or join a team to configure access control settings.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const roleOptions = Object.entries(rbacSettings.roles || {})
    .filter(([key]) => key !== 'owner')
    .map(([key, value]) => ({
      value: key,
      label: value.name || key,
      description: value.description
    }));

  const metadata = rbacSettings.metadata || {};
  const featureMetadata = metadata.features || {};
  const actionMetadata = metadata.actions || {};

  const featureCategories = {};
  Object.entries(featureMetadata).forEach(([key, meta]) => {
    const category = meta.category || 'other';
    if (!featureCategories[category]) {
      featureCategories[category] = [];
    }
    featureCategories[category].push({ key, ...meta });
  });

  const categoryNames = {
    core: 'Core Features',
    communication: 'Communication',
    real_estate: 'Real Estate',
    marketing: 'Marketing',
    automation: 'Automation',
    data: 'Data & Import',
    analytics: 'Analytics',
    admin: 'Administration'
  };

  const getIcon = (iconName) => {
    const IconComponent = ICONS[iconName];
    return IconComponent ? <IconComponent size={16} /> : <Settings size={16} />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Role-Based Access Control
              </CardTitle>
              <CardDescription>
                Configure what each role can see and do in the application
              </CardDescription>
            </div>
            {rbacSettings.custom && (
              <Badge className="bg-blue-500">Custom Settings</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Select Role to Configure
              </label>
              <Select value={selectedRole} onValueChange={handleRoleChange}>
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-slate-500">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {rbacSettings.can_edit && (
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <>
                    <Button variant="outline" onClick={discardChanges} disabled={saving}>
                      Discard
                    </Button>
                    <Button onClick={savePermissions} disabled={saving}>
                      {saving ? (
                        <span className="animate-spin">⏳</span>
                      ) : (
                        <Save size={16} className="mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </>
                )}
                <Button 
                  variant="ghost" 
                  onClick={() => setShowResetDialog(true)}
                  className="text-slate-500"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Reset to Default
                </Button>
              </div>
            )}
          </div>
          
          {!rbacSettings.can_edit && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-700">
              <Lock size={16} />
              <span className="text-sm">Only team owners and admins can modify role permissions.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Tabs */}
      <Card>
        <CardContent className="p-0">
          <Tabs defaultValue="features" className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
              <TabsTrigger 
                value="features" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
              >
                <LayoutDashboard size={16} className="mr-2" />
                Feature Access
              </TabsTrigger>
              <TabsTrigger 
                value="data" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
              >
                <Eye size={16} className="mr-2" />
                Data Access
              </TabsTrigger>
              <TabsTrigger 
                value="actions" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
              >
                <Edit size={16} className="mr-2" />
                Actions
              </TabsTrigger>
            </TabsList>

            {/* Feature Access Tab */}
            <TabsContent value="features" className="p-6">
              <p className="text-sm text-slate-600 mb-6">
                Control which pages and features this role can access.
              </p>
              
              <div className="space-y-6">
                {Object.entries(featureCategories).map(([category, features]) => (
                  <div key={category}>
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                      {categoryNames[category] || category}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {features.map(feature => {
                        const isEnabled = editedPermissions.feature_access?.[feature.key] ?? false;
                        const isLocked = selectedRole === 'owner' || !rbacSettings.can_edit;
                        
                        return (
                          <div 
                            key={feature.key}
                            className={`flex items-center justify-between p-3 border rounded-lg ${
                              isEnabled ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-slate-50/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${isEnabled ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                                {getIcon(feature.icon)}
                              </div>
                              <span className={`text-sm font-medium ${isEnabled ? 'text-slate-700' : 'text-slate-500'}`}>
                                {feature.name}
                              </span>
                            </div>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(checked) => handleFeatureToggle(feature.key, checked)}
                              disabled={isLocked}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Data Access Tab */}
            <TabsContent value="data" className="p-6">
              <p className="text-sm text-slate-600 mb-6">
                Control what data this role can view and edit. Options: <strong>None</strong>, <strong>Own</strong> (created by them), <strong>Assigned</strong> (assigned to them), <strong>All</strong> (entire team).
              </p>
              
              <div className="space-y-4">
                {Object.entries(editedPermissions.data_access || {}).map(([resource, permissions]) => (
                  <div key={resource} className="border rounded-lg p-4">
                    <h4 className="font-medium text-slate-700 mb-4 capitalize flex items-center gap-2">
                      {resource === 'contacts' && <Users size={18} />}
                      {resource === 'deals' && <KanbanSquare size={18} />}
                      {resource === 'calls' && <Phone size={18} />}
                      {resource === 'brokers' && <Briefcase size={18} />}
                      {resource === 'developers' && <Building2 size={18} />}
                      {resource}
                    </h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* View Permission */}
                      <div>
                        <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
                          <Eye size={12} /> View
                        </label>
                        <Select 
                          value={permissions.view || 'none'}
                          onValueChange={(value) => handleDataAccessChange(resource, 'view', value)}
                          disabled={!rbacSettings.can_edit}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="own">Own</SelectItem>
                            <SelectItem value="assigned">Assigned</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Edit Permission */}
                      <div>
                        <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
                          <Edit size={12} /> Edit
                        </label>
                        <Select 
                          value={permissions.edit || 'none'}
                          onValueChange={(value) => handleDataAccessChange(resource, 'edit', value)}
                          disabled={!rbacSettings.can_edit}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="own">Own</SelectItem>
                            <SelectItem value="assigned">Assigned</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Delete Permission */}
                      {permissions.delete !== undefined && (
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
                            <Trash2 size={12} /> Delete
                          </label>
                          <div className="h-9 flex items-center">
                            <Switch
                              checked={permissions.delete || false}
                              onCheckedChange={(checked) => handleDataAccessChange(resource, 'delete', checked)}
                              disabled={!rbacSettings.can_edit}
                            />
                            <span className="ml-2 text-sm text-slate-600">
                              {permissions.delete ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Assign Permission */}
                      {permissions.assign !== undefined && (
                        <div>
                          <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1">
                            <UserPlus size={12} /> Assign
                          </label>
                          <div className="h-9 flex items-center">
                            <Switch
                              checked={permissions.assign || false}
                              onCheckedChange={(checked) => handleDataAccessChange(resource, 'assign', checked)}
                              disabled={!rbacSettings.can_edit}
                            />
                            <span className="ml-2 text-sm text-slate-600">
                              {permissions.assign ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Actions Tab */}
            <TabsContent value="actions" className="p-6">
              <p className="text-sm text-slate-600 mb-6">
                Control specific actions this role can perform.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(editedPermissions.actions || {}).map(([action, enabled]) => {
                  const meta = actionMetadata[action] || { name: action.replace(/_/g, ' '), category: 'other' };
                  
                  return (
                    <div 
                      key={action}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        enabled ? 'border-green-200 bg-green-50/50' : 'border-slate-200'
                      }`}
                    >
                      <div>
                        <span className="font-medium text-slate-700 capitalize">{meta.name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {meta.category}
                        </Badge>
                      </div>
                      <Switch
                        checked={enabled}
                        onCheckedChange={(checked) => handleActionToggle(action, checked)}
                        disabled={!rbacSettings.can_edit}
                      />
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              Reset Permissions
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to reset permissions for <strong>{rbacSettings.roles[selectedRole]?.name || selectedRole}</strong> to the default settings? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={resetToDefault} disabled={saving}>
              {saving ? 'Resetting...' : 'Reset to Default'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RBACSettings;
